import type { FieldValues, Path, PathValue, UseFormSetValue } from "react-hook-form";
import { standardCourseDocumentTaskPrefillMapping } from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { splitSelectedCourse } from "@/src/utils/admissionOffice/splitSelectedCourse";
import { resolveDegreeValue } from "@/src/utils/admissionOffice/buildDegreeSelectOptions";
import type {
  AdmissionTaskPrefillFormBaseline,
  AdmissionTaskPrefillMapping,
  AdmissionTaskPrefillPayload,
} from "@/src/hooks/useAdmissionTaskPrefill";

export interface ApplyAdmissionTaskPrefillResult {
  finalMapping: AdmissionTaskPrefillMapping[];
  formBaseline: AdmissionTaskPrefillFormBaseline;
  usedMappings: string[];
  skippedMappings: string[];
}

export interface ApplyAdmissionTaskPrefillOptions<TForm extends FieldValues> {
  payload: AdmissionTaskPrefillPayload;
  setValue: UseFormSetValue<TForm>;
  mapping?: AdmissionTaskPrefillMapping[];
}

export const applyAdmissionTaskPrefillToForm = <TForm extends FieldValues>({
  payload,
  setValue,
  mapping,
}: ApplyAdmissionTaskPrefillOptions<TForm>): ApplyAdmissionTaskPrefillResult => {
  const baseMapping = mapping ?? standardCourseDocumentTaskPrefillMapping;

  const hasTaskIdMapping = baseMapping.some((item) => item.payloadKey === "task_id");
  const finalMapping: AdmissionTaskPrefillMapping[] = hasTaskIdMapping
    ? baseMapping
    : [...baseMapping, { formField: "task_id", payloadKey: "task_id" }];

  const usedMappings: string[] = [];
  const skippedMappings: string[] = [];
  const formBaseline: AdmissionTaskPrefillFormBaseline = {};

  finalMapping.forEach(({ formField, payloadKey, transform }) => {
    const rawValue = payload[payloadKey];
    const value = transform
      ? transform(rawValue)
      : rawValue == null
        ? ""
        : typeof rawValue === "string"
          ? rawValue
          : String(rawValue);
    formBaseline[formField] = value.trim();

    if (!value.trim()) {
      skippedMappings.push(`${String(payloadKey)} -> ${String(formField)} (empty)`);
      return;
    }
    setValue(formField as Path<TForm>, value as PathValue<TForm, Path<TForm>>, {
      shouldDirty: true,
      shouldTouch: true,
    });
    usedMappings.push(`${String(payloadKey)} -> ${String(formField)}`);
  });

  if (formBaseline.selectedCourse) {
    const { degree: splitDegree, selectedCourse } = splitSelectedCourse(formBaseline.selectedCourse);
    formBaseline.selectedCourse = selectedCourse;
    const degree = resolveDegreeValue(formBaseline.degree?.trim() || splitDegree);
    formBaseline.degree = degree;
    setValue("degree" as Path<TForm>, degree as PathValue<TForm, Path<TForm>>, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue("selectedCourse" as Path<TForm>, selectedCourse as PathValue<TForm, Path<TForm>>, {
      shouldDirty: true,
      shouldTouch: true,
    });
  } else {
    const degree = resolveDegreeValue(formBaseline.degree);
    formBaseline.degree = degree;
    setValue("degree" as Path<TForm>, degree as PathValue<TForm, Path<TForm>>, {
      shouldDirty: false,
      shouldTouch: false,
    });
  }

  return { finalMapping, formBaseline, usedMappings, skippedMappings };
};
